/**
 * The Overview's one framed panel, as the hero shows it: a header row (Overview, the scope, Live
 * with the clock, Pause live), then the vitals, then the flow with the activity beside it.
 */
import { Pause, Play } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { Button, cn, formatClock, useNow } from '@orgus/ui';
import { useOverviewPause } from './useHero.ts';

export function LiveFrame({ children, className }: { children: ReactNode; className?: string }) {
  const { paused, toggle } = useOverviewPause();
  const now = useNow(paused ? 60_000 : 15_000);
  return (
    <section aria-label="Live overview" className={cn('flex min-w-0 flex-col gap-4 rounded-card border border-border bg-surface p-4 shadow-elevation-flat sm:gap-6 sm:p-6', className)}>
      <header className="flex min-w-0 items-center justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-ui">
          <span className="font-medium text-fg">Overview</span>
          <span aria-hidden className="hidden h-4 w-px bg-border sm:inline" />
          <span className="hidden truncate text-fg-subtle sm:inline">All projects</span>
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <span className="flex items-center gap-2 text-caption text-fg-subtle" aria-live="polite">
            <span aria-hidden className={cn('size-2 rounded-pill', paused ? 'bg-border-strong' : 'bg-status-success-fill')} />
            <span>{paused ? 'Paused' : 'Live'}</span>
            <span className="hidden font-mono num sm:inline">{formatClock(now)}</span>
          </span>
          <Button variant="ghost" size="sm" icon={paused ? <Play /> : <Pause />} aria-pressed={paused} aria-label={paused ? 'Resume live' : 'Pause live'} onClick={toggle}>
            <span className="hidden sm:inline">{paused ? 'Resume live' : 'Pause live'}</span>
          </Button>
        </div>
      </header>
      {children}
    </section>
  );
}
